import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const InterLocationTransfer = ({ sourceLocationId, locations, products, onTransferComplete }) => {
  const [targetLocationId, setTargetLocationId] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [transferHistory, setTransferHistory] = useState([]);

  const availableTargets = locations?.filter(loc => loc?.id !== sourceLocationId);
  const selectedProductData = products?.find(p => p?.id === selectedProduct);

  const handleTransfer = async () => {
    if (!selectedProduct || !targetLocationId || quantity <= 0) return;

    setTransferring(true);
    // Simulate transfer (in real implementation, this would call a Supabase function)
    setTimeout(() => {
      const transfer = {
        id: Date.now(),
        product: selectedProductData?.name,
        quantity,
        from: locations?.find(l => l?.id === sourceLocationId)?.name,
        to: locations?.find(l => l?.id === targetLocationId)?.name,
        notes,
        timestamp: new Date()?.toISOString()
      };
      setTransferHistory(prev => [transfer, ...prev]);
      setSelectedProduct('');
      setQuantity(1);
      setNotes('');
      setTransferring(false);
      onTransferComplete();
    }, 1000);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Transfer Form */}
      <div className="card">
        <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="ArrowLeftRight" size={24} color="var(--color-primary)" />
          Create Transfer Request
        </h2>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              From Location
            </label>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm font-medium text-foreground">
                {locations?.find(l => l?.id === sourceLocationId)?.name}
              </p>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              To Location
            </label>
            <Select
              value={targetLocationId}
              onChange={(e) => setTargetLocationId(e?.target?.value)}
              className="w-full"
            >
              <option value="">Select destination...</option>
              {availableTargets?.map(location => (
                <option key={location?.id} value={location?.id}>
                  {location?.name} - {location?.city}, {location?.state}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Product
            </label>
            <Select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e?.target?.value)}
              className="w-full"
            >
              <option value="">Select product...</option>
              {products?.filter(p => p?.stock_quantity > 0)?.map(product => (
                <option key={product?.id} value={product?.id}>
                  {product?.name} (Stock: {product?.stock_quantity})
                </option>
              ))}
            </Select>
          </div>

          {selectedProductData && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded bg-muted flex items-center justify-center">
                  <Icon name="Package" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{selectedProductData?.name}</p>
                  <p className="text-xs text-muted-foreground">Available: {selectedProductData?.stock_quantity} units</p>
                  <p className="text-xs text-muted-foreground">Price: ${selectedProductData?.price?.toFixed(2)}</p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Quantity
            </label>
            <Input
              type="number"
              min="1"
              max={selectedProductData?.stock_quantity || 1}
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e?.target?.value) || 1)}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              rows="3"
              placeholder="Add any notes about this transfer..."
            />
          </div>

          <Button
            onClick={handleTransfer}
            disabled={transferring || !selectedProduct || !targetLocationId || quantity <= 0}
            className="w-full"
            iconName="Send"
            iconPosition="left"
          >
            {transferring ? 'Processing...' : 'Request Transfer'}
          </Button>
        </div>
      </div>
      {/* Transfer History */}
      <div className="card">
        <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="History" size={24} color="var(--color-accent)" />
          Recent Transfers
        </h2>

        {transferHistory?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="ArrowLeftRight" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-muted-foreground">No transfers yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transferHistory?.map(transfer => (
              <div key={transfer?.id} className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground mb-1">{transfer?.product}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{transfer?.from}</span>
                      <Icon name="ArrowRight" size={12} />
                      <span>{transfer?.to}</span>
                    </div>
                  </div>
                  <span className="px-2 py-1 rounded text-xs font-medium bg-success/10 text-success">
                    Completed
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Qty: {transfer?.quantity}</span>
                  <span className="text-muted-foreground">
                    {new Date(transfer?.timestamp)?.toLocaleString()}
                  </span>
                </div>
                {transfer?.notes && (
                  <p className="text-xs text-muted-foreground mt-2 italic">{transfer?.notes}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InterLocationTransfer;